/*
 * Keep Spreadconnect collection-card images aligned with sellable colors.
 * If a featured image belongs to a fully unavailable color, Wonder selects
 * the first available color swatch. Fully sold-out products are unchanged.
 */
(() => {
  const hasSelectedCollectionColorFilter = () =>
    new URLSearchParams(window.location.search).has("filter.v.t.shopify.color-pattern");

  const getSelectedCollectionColorFilterSignature = () =>
    new URLSearchParams(window.location.search)
      .getAll("filter.v.t.shopify.color-pattern")
      .sort()
      .join("|");

  let lastCollectionColorFilterSignature = null;

  const setCollectionColorFilterPending = (isPending, hasColor = false) => {
    document.documentElement.toggleAttribute("data-jp-merch-filter-pending", isPending);
    document.documentElement.toggleAttribute("data-jp-merch-color-active", isPending && hasColor);
  };

  // The critical head script sets first-paint pending state. Keep it until the
  // query-scoped model resolves; clearing it here would flash stale claims.
  document.addEventListener("jp:merch-filter-pending", (event) => {
    setCollectionColorFilterPending(true, Boolean(event.detail?.hasColor));
  });
  document.addEventListener("input", (event) => {
    if (!event.target.closest?.("#FacetFiltersForm")) return;
    const hasColor = [...document.querySelectorAll(
      '#FacetFiltersForm input[name="filter.v.t.shopify.color-pattern"]:checked',
    )].length > 0;
    setCollectionColorFilterPending(true, hasColor);
  }, true);

  /*
   * Every currently populated Spreadconnect card hover is a model, lifestyle,
   * or alternate-detail asset that needs the collection-card inset. Follow
   * Wonder's rendered swatch-to-hover relationship instead of maintaining a
   * second product/color allowlist that can miss new or resynchronized media.
   */

  /*
   * Keep shared merch colors in a stable, brand-led order on collection cards
   * and product pages without changing supplier-owned Shopify variant order.
   * Product-specific arrays avoid pretending that finish names and two-tone
   * hats belong to one universal color spectrum.
   */
  const swatchOrders = new Map([
    ["i-need-a-huge-jar-of-pickles-fitted-cotton-poly-t-shirt", ["washed yellow", "military green", "sweet cream heather", "heather gray", "white", "light blue", "burgundy"]],
    ["josh-s-pickles-icon-powered-by-pickles-holiday-ornament", ["white"]],
    ["powered-by-pickles-fanny-pack", ["black"]],
    ["joshs-pickles-icon-hip-bag", ["sand", "navy", "black", "gray"]],
    ["joshs-pickles-logo-youth-apron", ["black", "white", "red"]],
    [
      "joshs-pickles-unisex",
      ["black", "true red", "military green", "khaki", "sweet cream heather", "rock", "powder blue", "white"],
    ],
    [
      "joshs-pickles-womens",
      ["black", "red", "off white", "blue dusk", "white"],
    ],
    [
      "joshs-pickles-hoodie",
      ["black", "forest green", "sand", "asphalt gray", "navy"],
    ],
    [
      "baseball-3-4-length-sleeve-t-shirt",
      ["white/black", "white/red", "white/kelly green", "white/charcoal", "white/navy"],
    ],
    ["joshs-pickles-beanie", ["black", "olive", "charcoal grey", "navy"]],
    ["powered-by-pickles", ["black", "dark red", "gravel", "navy", "purple"]],
    ["powered-by-pickles-baby-onesie", ["rose", "mint", "light blue", "pastel yellow", "white"]],
    ["joshs-pickles-sticker", ["white matte"]],
    ["i-need-a-hug", ["khaki", "gravel", "powder blue", "white"]],
    [
      "womens-i-need-a-huge-jar-of-pickles",
      ["off white", "light blue", "white"],
    ],
    ["unisex-classic-t-shirt", ["cornsilk", "rock", "powder blue", "white"]],
    [
      "joshs-pickles-logo-sticker",
      ["white matte", "white glossy", "transparent glossy"],
    ],
    [
      "joshs-pickles-logo-snapback-baseball-cap",
      ["black", "dark grey", "heather gray", "navy", "white"],
    ],
    [
      "joshs-pickles-logo-trucker-hat",
      [
        "black/black",
        "multicam\\green",
        "multicam\\tan",
        "multicam\\black",
        "gray/black",
        "black/white",
        "white/kelly green",
        "white/red",
        "navy/white",
      ],
    ],
    [
      "joshs-pickles-logo-kids-trucker-cap",
      ["black", "navy", "white/black", "white/navy", "white/red"],
    ],
    [
      "joshs-pickles-logo-kids-hoodie",
      ["black", "burgundy", "khaki green", "charcoal grey", "heather denim", "navy"],
    ],
    [
      "joshs-pickles-apron",
      ["natural", "black", "burgundy", "charcoal", "forest green", "navy"],
    ],
  ]);

  /*
   * Merch-grid defaults intentionally rotate through the assortment. Clicking
   * the preferred swatch also updates Wonder's card link, so the product page
   * opens with the same variant selected instead of surprising the shopper.
   */
  const preferredCollectionColors = new Map([
    ["i-need-a-huge-jar-of-pickles-fitted-cotton-poly-t-shirt", "washed yellow"],
    ["josh-s-pickles-icon-powered-by-pickles-holiday-ornament", "white"],
    ["powered-by-pickles-fanny-pack", "black"],
    ["joshs-pickles-icon-hip-bag", "sand"],
    ["joshs-pickles-logo-youth-apron", "black"],
    ["joshs-pickles-unisex", "black"],
    ["joshs-pickles-womens", "black"],
    ["joshs-pickles-hoodie", "sand"],
    ["baseball-3-4-length-sleeve-t-shirt", "white/kelly green"],
    ["joshs-pickles-beanie", "olive"],
    ["powered-by-pickles", "dark red"],
    ["powered-by-pickles-baby-onesie", "rose"],
    ["joshs-pickles-sticker", "white matte"],
    ["i-need-a-hug", "powder blue"],
    ["womens-i-need-a-huge-jar-of-pickles", "off white"],
    ["unisex-classic-t-shirt", "cornsilk"],
    ["joshs-pickles-logo-sticker", "transparent glossy"],
    ["joshs-pickles-logo-snapback-baseball-cap", "navy"],
    ["joshs-pickles-logo-trucker-hat", "multicam/green"],
    ["joshs-pickles-logo-kids-trucker-cap", "white/red"],
    ["joshs-pickles-logo-kids-hoodie", "burgundy"],
    ["joshs-pickles-apron", "natural"],
  ]);

  /*
   * Shopify's category Color filter uses broad taxonomy families while
   * Spreadconnect keeps merchant-facing shade names on the Color option.
   * The option is supplier-owned and cannot be connected to those taxonomy
   * values, so tell the merch card which exact shade represents each family.
   */
  const filteredCollectionColors = new Map([
    ["i-need-a-huge-jar-of-pickles-fitted-cotton-poly-t-shirt", new Map([["14", "washed yellow"], ["9", "military green"], ["6", "sweet cream heather"], ["8", "heather gray"], ["3", "white"], ["2", "light blue"], ["13", "burgundy"]])],
    ["josh-s-pickles-icon-powered-by-pickles-holiday-ornament", new Map([["3", "white"]])],
    ["powered-by-pickles-fanny-pack", new Map([["1", "black"]])],
    ["joshs-pickles-icon-hip-bag", new Map([["6", "sand"], ["15", "navy"], ["1", "black"], ["8", "gray"]])],
    ["joshs-pickles-logo-youth-apron", new Map([["1", "black"], ["3", "white"], ["13", "red"]])],
    [
      "joshs-pickles-unisex",
      new Map([
        ["1", "black"],
        ["2", "powder blue"],
        ["3", "white"],
        ["6", "sweet cream heather"],
        ["7", "khaki"],
        ["8", "rock"],
        ["9", "military green"],
        ["13", "true red"],
      ]),
    ],
    [
      "joshs-pickles-womens",
      new Map([
        ["1", "black"],
        ["2", "blue dusk"],
        ["3", "white"],
        ["6", "off white"],
        ["13", "red"],
        ["15", "blue dusk"],
      ]),
    ],
    [
      "joshs-pickles-hoodie",
      new Map([
        ["1", "black"],
        ["6", "sand"],
        ["8", "asphalt gray"],
        ["9", "forest green"],
        ["15", "navy"],
      ]),
    ],
    [
      "baseball-3-4-length-sleeve-t-shirt",
      new Map([
        ["1", "white/black"],
        ["3", "white/black"],
        ["8", "white/charcoal"],
        ["9", "white/kelly green"],
        ["13", "white/red"],
        ["15", "white/navy"],
      ]),
    ],
    [
      "joshs-pickles-beanie",
      new Map([
        ["1", "black"],
        ["8", "charcoal grey"],
        ["9", "olive"],
        ["15", "navy"],
      ]),
    ],
    [
      "powered-by-pickles",
      new Map([
        ["1", "black"],
        ["8", "gravel"],
        ["12", "purple"],
        ["13", "dark red"],
        ["15", "navy"],
      ]),
    ],
    ["powered-by-pickles-baby-onesie", new Map([["2", "light blue"], ["3", "white"], ["9", "mint"], ["11", "rose"], ["14", "pastel yellow"]])],
    ["joshs-pickles-sticker", new Map([["3", "white matte"]])],
    [
      "i-need-a-hug",
      new Map([
        ["2", "powder blue"],
        ["3", "white"],
        ["7", "khaki"],
        ["8", "gravel"],
      ]),
    ],
    [
      "womens-i-need-a-huge-jar-of-pickles",
      new Map([
        ["2", "light blue"],
        ["3", "white"],
        ["6", "off white"],
      ]),
    ],
    [
      "unisex-classic-t-shirt",
      new Map([
        ["2", "powder blue"],
        ["3", "white"],
        ["8", "rock"],
        ["14", "cornsilk"],
      ]),
    ],
    [
      "joshs-pickles-logo-snapback-baseball-cap",
      new Map([
        ["1", "black"],
        ["3", "white"],
        ["8", "dark grey"],
        ["15", "navy"],
      ]),
    ],
    [
      "joshs-pickles-logo-trucker-hat",
      new Map([
        ["1", "black/black"],
        ["3", "black/white"],
        ["6", "multicam\\tan"],
        ["8", "gray/black"],
        ["9", "multicam\\green"],
        ["13", "white/red"],
        ["15", "navy/white"],
      ]),
    ],
    [
      "joshs-pickles-logo-kids-trucker-cap",
      new Map([
        ["1", "black"],
        ["3", "white/black"],
        ["13", "white/red"],
        ["15", "navy"],
      ]),
    ],
    [
      "joshs-pickles-logo-kids-hoodie",
      new Map([
        ["1", "black"],
        ["8", "charcoal grey"],
        ["9", "khaki green"],
        ["13", "burgundy"],
        ["15", "navy"],
      ]),
    ],
    [
      "joshs-pickles-apron",
      new Map([
        ["1", "black"],
        ["6", "natural"],
        ["8", "charcoal"],
        ["9", "forest green"],
        ["13", "burgundy"],
        ["15", "navy"],
      ]),
    ],
  ]);
  const imageKey = (value) => {
    if (!value) return "";

    try {
      const url = new URL(value, window.location.href);
      const version = url.searchParams.get("v");
      return `${url.pathname}${version ? `?v=${version}` : ""}`;
    } catch (_error) {
      return value;
    }
  };

  const syncCardHoverInset = (card) => {
    if (!card.querySelector("[data-jp-spreadconnect]")) return;

    const hoverImage = card.querySelector(".card__img--hover");
    if (!hoverImage || hoverImage.hasAttribute("data-jp-hover-disabled")) {
      card.removeAttribute("data-jp-hover-inset");
      return;
    }

    const currentHover = imageKey(hoverImage.getAttribute("src"));
    const matchesLinkedHover = [...card.querySelectorAll(".color-swatcher[data-hover]")].some(
      (swatch) =>
        Boolean(swatch.dataset.hover?.trim()) &&
        currentHover === imageKey(swatch.dataset.hover),
    );
    card.toggleAttribute("data-jp-hover-inset", matchesLinkedHover);
  };

  // Describe the image actually rendered, not a stale server/default shade or
  // an active swatch whose replacement image has not committed yet.
  const syncCardImageLabels = (card) => {
    if (!card.querySelector("[data-jp-spreadconnect]")) return;
    const title = card.querySelector("h3")?.textContent?.trim();
    if (!title) return;
    const swatches = [...card.querySelectorAll(".color-swatcher[data-img]")];
    const primary = card.querySelector(
      ".card__img:not(.card__img--hover):not([data-jp-hover-image])",
    );
    const primaryKey = imageKey(primary?.getAttribute("src"));
    const primarySwatch = swatches.find((s) => primaryKey && imageKey(s.dataset.img) === primaryKey);
    if (primarySwatch?.dataset.color) primary.alt = `${title} - ${primarySwatch.dataset.color}`;
    const hover = card.querySelector("[data-jp-hover-image], .card__img--hover");
    const hoverKey = imageKey(hover?.getAttribute("src"));
    const hoverSwatch = swatches.find((s) => s.dataset.hover && hoverKey && imageKey(s.dataset.hover) === hoverKey);
    if (hoverSwatch?.dataset.color && !hover.hasAttribute("data-jp-hover-disabled")) {
      hover.alt = `${title} - ${hoverSwatch.dataset.color}`;
    }
  };

  const syncCardImage = (card) => {
    if (!card.querySelector("[data-jp-spreadconnect]")) return;

    const image = card.querySelector(".card__img");
    if (!image) return;

    const currentImages = new Set(
      [image.getAttribute("src"), image.currentSrc].map(imageKey).filter(Boolean),
    );
    const unavailableSwatches = card.querySelectorAll(
      ".color-swatcher--wrapper.unavailable .color-swatcher[data-img]",
    );
    const featuredColorIsUnavailable = [...unavailableSwatches].some((swatch) =>
      currentImages.has(imageKey(swatch.dataset.img)),
    );

    if (!featuredColorIsUnavailable) return;

    const firstAvailableSwatch = card.querySelector(
      ".color-swatcher--wrapper:not(.unavailable) .color-swatcher[data-img]:not([data-img=''])",
    );
    firstAvailableSwatch?.closest(".color-swatcher--wrapper")?.click();
  };

  const getCardHandle = (card) => card.querySelector(".card__picture")?.dataset.uri;

  const getDisplayedCardSwatch = (card) => {
    const activeSwatch = card.querySelector(
      ".color-swatcher--wrapper.active .color-swatcher[data-img]",
    );
    if (activeSwatch) return activeSwatch;

    const primaryImage = card.querySelector(
      ".card__img:not(.card__img--hover):not([data-jp-hover-image])",
    );
    const currentImages = new Set(
      [primaryImage?.getAttribute("src"), primaryImage?.currentSrc]
        .map(imageKey)
        .filter(Boolean),
    );
    return [...card.querySelectorAll(".color-swatcher[data-img]")].find((swatch) =>
      currentImages.has(imageKey(swatch.dataset.img)),
    );
  };

  const selectDisplayedOrFirstAvailableColor = (card) => {
    const displayedSwatch = getDisplayedCardSwatch(card);
    const displayedWrapper = displayedSwatch?.closest(".color-swatcher--wrapper");
    const swatch =
      displayedWrapper && !displayedWrapper.classList.contains("unavailable")
        ? displayedSwatch
        : card.querySelector(
            ".color-swatcher--wrapper:not(.unavailable) .color-swatcher[data-color]",
          );

    return swatch ? selectCardColor(card, swatch.dataset.color) : false;
  };

  /*
   * Wonder retains the previous card hover image when a selected swatch has no
   * data-hover value. That can show a different garment color on hover. Keep
   * hover swapping enabled only while the displayed swatch has a verified
   * secondary image; otherwise the selected primary remains visible.
   */
  const syncCardHoverImage = (card, requestedSwatch) => {
    if (!card.querySelector("[data-jp-spreadconnect]")) return;

    const hoverImage = card.querySelector("[data-jp-hover-image], .card__img--hover");
    if (!hoverImage) return;
    hoverImage.setAttribute("data-jp-hover-image", "");
    syncCardImageLabels(card);

    const swatch = requestedSwatch || getDisplayedCardSwatch(card);
    const hoverSrc = swatch?.dataset.hover?.trim();
    if (!hoverSrc) {
      /*
       * Wonder's hover selector is presence-based: as long as this class is in
       * the card, hovering fades the primary even when the secondary element
       * itself is hidden. Remove the class as well as hiding the stale layer so
       * a no-hover swatch cannot produce an empty card.
       */
      hoverImage.classList.remove("card__img--hover");
      hoverImage.setAttribute("data-jp-hover-disabled", "");
      hoverImage.setAttribute("aria-hidden", "true");
      hoverImage.style.setProperty("display", "none", "important");
      card.removeAttribute("data-jp-hover-inset");
      return;
    }

    hoverImage.classList.add("card__img--hover");
    hoverImage.removeAttribute("data-jp-hover-disabled");
    hoverImage.removeAttribute("aria-hidden");
    hoverImage.style.removeProperty("display");
    hoverImage.setAttribute("src", hoverSrc);
    const hoverSrcset = swatch.dataset.hoverSrcset?.trim();
    if (hoverSrcset) hoverImage.setAttribute("srcset", hoverSrcset);
    syncCardHoverInset(card);
    syncCardImageLabels(card);
  };

  const bindCardHoverSync = (card) => {
    const container = card.querySelector(
      ".card__color-swatcher--container[data-jp-spreadconnect]",
    );
    if (!container || container.hasAttribute("data-jp-hover-sync")) return;
    container.setAttribute("data-jp-hover-sync", "");

    const syncRequestedSwatch = (event) => {
      const wrapper = event.target.closest?.(".color-swatcher--wrapper");
      const swatch = wrapper?.querySelector(".color-swatcher[data-img]");
      if (!swatch) return;
      requestAnimationFrame(() => syncCardHoverImage(card, swatch));
    };
    container.addEventListener("mouseover", syncRequestedSwatch);
    container.addEventListener("click", (event) => {
      const filteredVariantId = event.target
        .closest?.(".color-swatcher--wrapper")
        ?.querySelector(".color-swatcher")?.dataset.jpSizeVariant;
      requestAnimationFrame(() => {
        syncCardHoverImage(card);
        if (filteredVariantId) {
          updateCardVariantLinks(card, filteredVariantId);
          card.dataset.jpFilteredSizeVariant = filteredVariantId;
        }
      });
    });
    container.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        requestAnimationFrame(() => syncCardHoverImage(card));
      }
    });
    container.addEventListener("mouseleave", () => {
      requestAnimationFrame(() => syncCardHoverImage(card));
    });
  };

  /*
   * Wonder's card click handler can navigate with the swatch URL it cached
   * before our filtered-size correction, even while both rendered anchors
   * show the right variant. Resolve the destination again in the capture
   * phase so every image/title click uses the active filtered-size variant.
   */
  const bindFilteredSizeCardNavigation = (card) => {
    if (card.hasAttribute("data-jp-size-navigation")) return;
    card.setAttribute("data-jp-size-navigation", "");

    card.addEventListener(
      "click",
      (event) => {
        const link = event.target.closest?.('a[href*="/products/"]');
        const variantId = card.dataset.jpFilteredSizeVariant;
        if (!link || !variantId) return;

        const url = new URL(link.getAttribute("href"), window.location.origin);
        url.searchParams.set("variant", variantId);
        link.setAttribute("href", `${url.pathname}${url.search}${url.hash}`);

        const isPlainPrimaryClick =
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey;
        if (isPlainPrimaryClick) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.location.assign(url.href);
        } else {
          event.stopImmediatePropagation();
        }
      },
      true,
    );
  };

  const orderCardSwatches = (card) => {
    const order = swatchOrders.get(getCardHandle(card));
    const container = card.querySelector(".card__color-swatcher--container[data-jp-spreadconnect]");
    if (!order || !container) return;

    const ranks = new Map(order.map((color, index) => [normalizeLabel(color), index]));
    const wrappers = [...container.querySelectorAll(":scope > .color-swatcher--wrapper")];
    wrappers
      .sort((left, right) => {
        const leftColor = normalizeLabel(left.querySelector(".color-swatcher")?.dataset.color);
        const rightColor = normalizeLabel(right.querySelector(".color-swatcher")?.dataset.color);
        return (ranks.get(leftColor) ?? 999) - (ranks.get(rightColor) ?? 999);
      })
      .forEach((wrapper) => {
        const color = normalizeLabel(wrapper.querySelector(".color-swatcher")?.dataset.color);
        wrapper.style.order = String(ranks.get(color) ?? 999);
        container.append(wrapper);
      });

    const counter = container.querySelector(".color-swatcher--counter");
    if (counter) container.append(counter);
    card.color_swatcher_wrappers = container.querySelectorAll(".color-swatcher--wrapper");
  };

  const renderUnavailableFilteredSwatch = (card, swatch, wrapper) => {
    card.querySelectorAll(".color-swatcher--wrapper.active").forEach((candidate) =>
      candidate.classList.remove("active"),
    );
    card.querySelectorAll(".color-swatcher[data-active-swatch]").forEach((candidate) =>
      candidate.removeAttribute("data-active-swatch"),
    );
    wrapper.classList.remove("hidden");
    wrapper.classList.add("active");
    swatch.setAttribute("data-active-swatch", "");

    const primaryImage = card.querySelector(
      ".card__picture .card__img:not(.card__img--hover):not([data-jp-hover-image])",
    );
    if (primaryImage && swatch.dataset.img) {
      primaryImage.setAttribute("src", swatch.dataset.img);
      if (swatch.dataset.srcset) primaryImage.setAttribute("srcset", swatch.dataset.srcset);
    }

    if (swatch.dataset.href) {
      card.querySelectorAll('a[href*="/products/"]').forEach((link) =>
        link.setAttribute("href", swatch.dataset.href),
      );
    }
  };

  const selectCardColor = (card, color, allowUnavailable = false) => {
    const swatch = [...card.querySelectorAll(".color-swatcher[data-color]")].find(
      (candidate) => normalizeLabel(candidate.dataset.color) === normalizeLabel(color),
    );
    const wrapper = swatch?.closest(".color-swatcher--wrapper");
    if (!wrapper || (!allowUnavailable && wrapper.classList.contains("unavailable"))) return false;

    if (allowUnavailable && wrapper.classList.contains("unavailable")) {
      renderUnavailableFilteredSwatch(card, swatch, wrapper);
      return true;
    }

    if (!wrapper.classList.contains("active")) {
      wrapper.click();
    }
    return wrapper.classList.contains("active");
  };

  const selectFilteredCollectionColor = (card) => {
    const familyColors = filteredCollectionColors.get(getCardHandle(card));
    if (!familyColors) return false;

    const selectedFamilies = new URLSearchParams(window.location.search)
      .getAll("filter.v.t.shopify.color-pattern")
      .map((value) => value.split("/").pop());

    return selectedFamilies.some((family) => selectCardColor(card, familyColors.get(family)));
  };

  /*
   * Shopify's Size filter returns a product when any variant has the selected
   * size, but Wonder keeps the card's ordinary merchandising color and links
   * to that color's first variant. Resolve the card to an actually available
   * variant whose Size matches the active filter. Keep the displayed color
   * when it has that size; otherwise use the first matching color in the
   * merchant-defined swatch order.
   */
  const collectionProductCache = new Map();

  const getSelectedCollectionSizes = (queryHref = window.location.href) =>
    new URL(queryHref, window.location.origin).searchParams
      .getAll("filter.v.option.size")
      .map(normalizeLabel)
      .filter(Boolean);

  const getCollectionProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!collectionProductCache.has(handle)) {
      collectionProductCache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: "application/json" },
        })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
          .then((product) => {
            if (!product) collectionProductCache.delete(handle);
            return product;
          }),
      );
    }
    return collectionProductCache.get(handle);
  };

  const getSelectedFilteredColors = (handle, queryHref = window.location.href) => {
    const familyColors = filteredCollectionColors.get(handle);
    if (!familyColors) return [];

    return new URL(queryHref, window.location.origin).searchParams
      .getAll("filter.v.t.shopify.color-pattern")
      .map((value) => familyColors.get(value.split("/").pop()))
      .filter(Boolean)
      .map(normalizeLabel);
  };

  const setCollectionCardFilteredVisibility = (card, isHidden) => {
    const cardContainer =
      card.closest(".collection__grid__item") || card.closest(".card__container") || card;
    cardContainer.setAttribute("data-jp-color-filter-resolved", "");
    cardContainer.toggleAttribute("hidden", isHidden);
    cardContainer.classList.toggle("jp-filtered-color-unavailable", isHidden);
    if (isHidden) {
      cardContainer.style.setProperty("display", "none", "important");
    } else {
      cardContainer.style.removeProperty("display");
    }
  };

  /*
   * Color filters have two independent sources of information: Shopify's
   * server response decides which products belong to the query, while public
   * product JSON decides whether the mapped supplier shade is purchasable.
   * Keep those inputs in one query-scoped model. Never derive a collection
   * count from cards that happen to be mounted during an AJAX transition.
   */
  const setTextIfChanged = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };

  const merchFacetPageHandleCache = new Map();
  let merchFilterGeneration = 0;
  let merchFilterScheduled = false;
  let activeMerchFilterQuery = null;
  let appliedMerchFilterQuery = null;
  let appliedMerchFilterModel = null;

  const getMerchFilterUrl = (includeColor, queryHref = window.location.href) => {
    const url = new URL(queryHref, window.location.origin);
    url.searchParams.delete("page");
    if (!includeColor) url.searchParams.delete("filter.v.t.shopify.color-pattern");
    return url;
  };

  const getMerchFilterQueryKey = (queryHref = window.location.href) =>
    getMerchFilterUrl(true, queryHref).href;

  const setMerchColorFacetCount = (valueLabel, count) => {
    const originalLabel = valueLabel.dataset.jpMerchOriginalCountLabel || valueLabel.textContent || "";
    setTextIfChanged(valueLabel, `${originalLabel.replace(/\s*\(\s*\d+\s*\)\s*$/, "")} (${count})`);
    delete valueLabel.dataset.jpMerchOriginalCountLabel;
    valueLabel.removeAttribute("aria-busy");
  };

  const getMerchFacetPage = (url) => {
    if (!merchFacetPageHandleCache.has(url)) {
      const sectionUrl = new URL(url);
      const sectionId = document.getElementById("product-grid")?.dataset.id;
      if (sectionId) sectionUrl.searchParams.set("section_id", sectionId);
      merchFacetPageHandleCache.set(
        url,
        fetch(sectionUrl.href, { headers: { Accept: "text/html" } })
          .then((response) => (response.ok ? response.text() : null))
          .then((markup) => {
            if (!markup) return null;
            const page = new DOMParser().parseFromString(markup, "text/html");
            const handles = [...page.querySelectorAll("#product-grid color-swatch")]
              .map(getCardHandle)
              .filter(Boolean);
            const nextLink = page.querySelector(
              '.wt-pagination a.wt-pagination__item--prev, load-more-pagination a[data-load-more]',
            );
            return { handles, nextUrl: nextLink?.getAttribute("href") || null };
          })
          .catch(() => null)
          .then((page) => {
            if (!page) merchFacetPageHandleCache.delete(url);
            return page;
          }),
      );
    }
    return merchFacetPageHandleCache.get(url);
  };

  const getAllMerchFacetPages = async (url) => {
    const handles = new Set();
    const visited = new Set();
    const pages = [];
    let nextUrl = url.href;
    while (nextUrl && !visited.has(nextUrl)) {
      visited.add(nextUrl);
      const page = await getMerchFacetPage(nextUrl);
      if (!page) return null;
      page.handles.forEach((handle) => handles.add(handle));
      pages.push({ url: nextUrl, handles: page.handles });
      if (page.nextUrl) {
        const next = new URL(page.nextUrl, url);
        const previewThemeId = url.searchParams.get("preview_theme_id");
        if (previewThemeId && !next.searchParams.has("preview_theme_id")) {
          next.searchParams.set("preview_theme_id", previewThemeId);
        }
        nextUrl = next.href;
      } else {
        nextUrl = null;
      }
    }
    return { handles: [...handles], pages };
  };

  const getSellableFamilies = (handle, product, selectedSizes) => {
    const mappedFamilies = filteredCollectionColors.get(handle);
    if (!mappedFamilies || !Array.isArray(product?.variants)) return null;
    const optionNames = (product.options || []).map((option) =>
      normalizeLabel(typeof option === "string" ? option : option?.name),
    );
    const colorIndex = optionNames.indexOf("color");
    const sizeIndex = optionNames.indexOf("size");
    if (colorIndex < 0) return new Set();
    return new Set(
      [...mappedFamilies].flatMap(([family, shade]) =>
        product.variants.some(
          (variant) =>
            variant.available &&
            normalizeLabel(variant.options?.[colorIndex]) === normalizeLabel(shade) &&
            (selectedSizes.length === 0 ||
              (sizeIndex >= 0 && selectedSizes.includes(normalizeLabel(variant.options?.[sizeIndex])))),
        )
          ? [family]
          : [],
      ),
    );
  };

  const parseCollectionTotal = (root = document) => {
    const text = root.querySelector("#ProductCount")?.textContent || "";
    return Number(text.match(/\d+\s+of\s+(\d+)\s+products/)?.[1]) || null;
  };

  const updateMerchFilterSurfaces = (model, root = document, queryHref = window.location.href) => {
    const selectedFamilies = model.selectedFamilies;
    root.querySelectorAll('#FacetFiltersForm input[name="filter.v.t.shopify.color-pattern"], #FacetFiltersFormMobile input[name="filter.v.t.shopify.color-pattern"]').forEach((input) => {
      const label = input.closest("label.form-checkbox")?.querySelector(".form-checkbox__label");
      if (!label) return;
      setMerchColorFacetCount(label, model.countsByFamily.get(input.value.split("/").pop()) || 0);
    });

    root.querySelectorAll("#ProductCount").forEach((counter) => {
      const total = parseCollectionTotal(root);
      const count = model.resultHandles.size;
      setTextIfChanged(counter, total ? `${count} of ${total} products` : `${count} products`);
    });

    const button = root.getElementById("FacetFiltersForm")
      ?.closest("#wt-filters-drawer")
      ?.querySelector('.wt-filter__footer__body .wt-cart__cta');
    if (button) {
      const text = `View results (${model.resultHandles.size})`;
      setTextIfChanged(button, text);
      button.setAttribute("aria-label", text);
    }

    root.querySelectorAll("#product-grid color-swatch").forEach((card) => {
      const handle = getCardHandle(card);
      setCollectionCardFilteredVisibility(card, selectedFamilies.length > 0 && !model.resultHandles.has(handle));
    });

    if (selectedFamilies.length > 0) {
      const queryUrl = new URL(queryHref, window.location.origin);
      const currentPage = Number(queryUrl.searchParams.get("page")) || 1;
      const eligiblePageNumbers = model.eligiblePages
        .map((page) => Number(new URL(page.url).searchParams.get("page")) || 1)
        .sort((left, right) => left - right);
      const eligible = new Set(eligiblePageNumbers);
      root.querySelectorAll(".wt-pagination__list li").forEach((item) => {
        const link = item.querySelector("a.wt-pagination__item");
        if (link?.classList.contains("wt-pagination__item--next")) {
          const previous = eligiblePageNumbers.filter((number) => number < currentPage).pop();
          item.hidden = !previous;
          if (previous) {
            const url = new URL(queryHref, window.location.origin);
            url.searchParams.set("page", String(previous));
            link.href = url.href;
          }
        } else if (link?.classList.contains("wt-pagination__item--prev")) {
          const next = eligiblePageNumbers.find((number) => number > currentPage);
          item.hidden = !next;
          if (next) {
            const url = new URL(queryHref, window.location.origin);
            url.searchParams.set("page", String(next));
            link.href = url.href;
          }
        } else {
          const displayedNumber = Number(item.textContent.trim());
          item.hidden = Number.isInteger(displayedNumber) && displayedNumber > 0 && !eligible.has(displayedNumber);
        }
      });
      const nextLoad = root.querySelector('load-more-pagination a[data-load-more]');
      const next = eligiblePageNumbers.find((number) => number > currentPage);
      if (nextLoad) {
        nextLoad.hidden = !next;
        if (next) {
          const url = new URL(queryHref, window.location.origin);
          url.searchParams.set("page", String(next));
          nextLoad.href = url.href;
        }
      }
    }
  };

  const buildMerchFilterModel = async (queryHref, root = document) => {
    const generation = ++merchFilterGeneration;
    const queryKey = getMerchFilterQueryKey(queryHref);
    const queryUrl = new URL(queryKey);
    const selectedFamilies = queryUrl.searchParams
      .getAll("filter.v.t.shopify.color-pattern")
      .map((value) => value.split("/").pop());
    const selectedSizes = getSelectedCollectionSizes(queryKey);
    const baseUrl = getMerchFilterUrl(false, queryKey);
    const selectedUrl = getMerchFilterUrl(true, queryKey);
    const familyInputs = [...root.querySelectorAll(
      'input[name="filter.v.t.shopify.color-pattern"]',
    )];
    const familyValueMap = new Map(familyInputs.map((input) => [
      input.value.split("/").pop(), input.value,
    ]));
    queryUrl.searchParams.getAll("filter.v.t.shopify.color-pattern")
      .forEach((value) => familyValueMap.set(value.split("/").pop(), value));
    const familyValues = [...familyValueMap.entries()];
    const [baseResult, selectedResult, familyResults] = await Promise.all([
      getAllMerchFacetPages(baseUrl),
      selectedFamilies.length ? getAllMerchFacetPages(selectedUrl) : Promise.resolve(null),
      Promise.all(familyValues.map(async ([family, value]) => {
        const familyUrl = new URL(baseUrl);
        familyUrl.searchParams.set("filter.v.t.shopify.color-pattern", value);
        return [family, await getAllMerchFacetPages(familyUrl)];
      })),
    ]);
    if (generation !== merchFilterGeneration) return null;
    if (!baseResult || (selectedFamilies.length && !selectedResult) ||
      familyResults.some(([, result]) => !result)) return null;

    const baseHandles = baseResult.handles;
    const selectedHandles = selectedResult?.handles;
    const products = await Promise.all(baseHandles.map((handle) => getCollectionProduct(handle)));
    if (generation !== merchFilterGeneration || products.some((product) => !product)) return null;

    const sellableFamiliesByHandle = new Map(
      baseHandles.map((handle, index) => [
        handle,
        getSellableFamilies(handle, products[index], selectedSizes),
      ]),
    );
    const eligibleHandlesByFamily = new Map(familyResults.map(([family, result]) => [
      family,
      new Set(result.handles.filter((handle) => {
        const sellable = sellableFamiliesByHandle.get(handle);
        return sellable === null || sellable?.has(family);
      })),
    ]));
    const countsByFamily = new Map([...eligibleHandlesByFamily].map(([family, handles]) => [
      family, handles.size,
    ]));
    const resultHandles = new Set(
      (selectedHandles || baseHandles).filter(
        (handle) =>
          selectedFamilies.length === 0 ||
          selectedFamilies.some((family) => eligibleHandlesByFamily.get(family)?.has(handle)),
      ),
    );
    const eligiblePages = (selectedResult || baseResult).pages
      .filter((page) => page.handles.some((handle) => resultHandles.has(handle)));
    const model = { queryKey, selectedFamilies, countsByFamily, resultHandles, eligiblePages };
    const currentPage = Number(queryUrl.searchParams.get("page")) || 1;
    const currentPageEligible = eligiblePages.some((page) =>
      (Number(new URL(page.url).searchParams.get("page")) || 1) === currentPage,
    );
    if (selectedFamilies.length && resultHandles.size && !currentPageEligible) {
      const firstPage = Number(new URL(eligiblePages[0].url).searchParams.get("page")) || 1;
      queryUrl.searchParams.set("page", String(firstPage));
      return { redirectUrl: queryUrl.href };
    }
    return { model, generation };
  };

  window.jpPrepareMerchFilterSection = async (html, searchParams) => {
    const queryUrl = new URL(window.location.pathname, window.location.origin);
    queryUrl.search = searchParams;
    const stagedDocument = new DOMParser().parseFromString(html, "text/html");
    const prepared = await buildMerchFilterModel(queryUrl.href, stagedDocument);
    if (!prepared) return null;
    if (prepared.redirectUrl) return { redirectUrl: prepared.redirectUrl };
    updateMerchFilterSurfaces(prepared.model, stagedDocument, queryUrl.href);
    return { html: stagedDocument.documentElement.outerHTML, queryUrl: queryUrl.href, ...prepared };
  };

  window.jpCommitMerchFilterSection = (prepared) => {
    if (prepared?.model) {
      appliedMerchFilterQuery = prepared.model.queryKey;
      appliedMerchFilterModel = prepared.model;
      // The drawer footer survives native facet replacement; commit it from
      // the same resolved model as the new grid, including no-Color queries.
      updateMerchFilterSurfaces(prepared.model, document, prepared.queryUrl);
    } else {
      appliedMerchFilterQuery = getMerchFilterQueryKey(prepared?.queryUrl || window.location.href);
      appliedMerchFilterModel = null;
    }
    activeMerchFilterQuery = null;
    document.documentElement.removeAttribute("data-jp-merch-initial-filter-pending");
    setCollectionColorFilterPending(false);
    enhanceMerchFilterDrawer();
  };

  const reconcileMerchFilterState = async () => {
    if (!document.body.classList.contains("template-collection-merch")) return;
    const queryKey = getMerchFilterQueryKey();
    const queryUrl = new URL(queryKey);
    activeMerchFilterQuery = queryKey;
    const prepared = await buildMerchFilterModel(queryKey);
    if (!prepared || prepared.redirectUrl || queryKey !== getMerchFilterQueryKey()) {
      activeMerchFilterQuery = null;
      document.documentElement.removeAttribute("data-jp-merch-initial-filter-pending");
      return;
    }
    updateMerchFilterSurfaces(prepared.model, document, queryKey);
    setCollectionColorFilterPending(false);
    appliedMerchFilterQuery = queryKey;
    appliedMerchFilterModel = prepared.model;
    activeMerchFilterQuery = null;
    document.documentElement.removeAttribute("data-jp-merch-initial-filter-pending");
    enhanceMerchFilterDrawer();
  };

  document.addEventListener("jp:merch-filter-failed", () => {
    document.documentElement.removeAttribute("data-jp-merch-initial-filter-pending");
    setCollectionColorFilterPending(false);
  });

  const scheduleMerchFilterReconciliation = () => {
    if (!document.body.classList.contains("template-collection-merch") || merchFilterScheduled) return;
    merchFilterScheduled = true;
    requestAnimationFrame(() => {
      merchFilterScheduled = false;
      const queryKey = getMerchFilterQueryKey();
      if (queryKey !== appliedMerchFilterQuery && queryKey !== activeMerchFilterQuery) {
        void reconcileMerchFilterState();
        return;
      }

      /*
       * Wonder may replace the grid after the query-scoped model has already
       * resolved (notably for a cached filter response). Reapply that same
       * immutable model to the replacement cards. Do not recompute from the
       * transient grid: it can contain supplier-stale results for a frame.
       */
      if (queryKey === appliedMerchFilterModel?.queryKey) {
        updateMerchFilterSurfaces(appliedMerchFilterModel);
      }
    });
  };

  const updateCardVariantLinks = (card, variantId) => {
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const url = new URL(link.getAttribute("href"), window.location.origin);
      url.searchParams.set("variant", String(variantId));
      link.setAttribute("href", `${url.pathname}${url.search}${url.hash}`);
    });
  };

  const filterCardSwatchesByAvailableColors = (card, variantsByColor) => {
    card.querySelectorAll(".color-swatcher--wrapper").forEach((wrapper) => {
      if (!wrapper.hasAttribute("data-jp-size-base-hidden")) {
        wrapper.dataset.jpSizeBaseHidden = String(wrapper.classList.contains("hidden"));
        wrapper.dataset.jpSizeBaseHiddenAttribute = String(wrapper.hasAttribute("hidden"));
        wrapper.dataset.jpSizeBaseDisplay = wrapper.style.getPropertyValue("display");
        wrapper.dataset.jpSizeBaseDisplayPriority =
          wrapper.style.getPropertyPriority("display");
      }
      const swatch = wrapper.querySelector(".color-swatcher[data-color]");
      const color = normalizeLabel(swatch?.dataset.color);
      const filteredVariant = variantsByColor.get(color);
      const shouldHide = !filteredVariant;
      wrapper.classList.toggle("hidden", shouldHide);
      wrapper.toggleAttribute("hidden", shouldHide);
      if (shouldHide) {
        wrapper.style.setProperty("display", "none", "important");
        if (swatch) delete swatch.dataset.jpSizeVariant;
      } else {
        wrapper.style.removeProperty("display");
        if (!swatch.hasAttribute("data-jp-size-base-href-attribute")) {
          swatch.dataset.jpSizeBaseHrefAttribute = String(swatch.hasAttribute("data-href"));
          swatch.dataset.jpSizeBaseHref = swatch.getAttribute("data-href") || "";
        }
        const url = new URL(swatch.getAttribute("data-href") || window.location.href, window.location.origin);
        url.searchParams.set("variant", String(filteredVariant.id));
        swatch.setAttribute("data-href", `${url.pathname}${url.search}${url.hash}`);
        swatch.dataset.jpSizeVariant = String(filteredVariant.id);
      }
    });

    const counter = card.querySelector(".color-swatcher--counter");
    if (counter) {
      if (!counter.hasAttribute("data-jp-size-base-hidden")) {
        counter.dataset.jpSizeBaseHidden = String(counter.classList.contains("hidden"));
        counter.dataset.jpSizeBaseHiddenAttribute = String(counter.hasAttribute("hidden"));
        counter.dataset.jpSizeBaseDisplay = counter.style.getPropertyValue("display");
        counter.dataset.jpSizeBaseDisplayPriority =
          counter.style.getPropertyPriority("display");
      }
      counter.classList.add("hidden");
      counter.setAttribute("hidden", "");
      counter.style.setProperty("display", "none", "important");
    }
  };

  const restoreCardSwatchesAfterSizeFilter = (card) => {
    card.querySelectorAll("[data-jp-size-base-hidden]").forEach((element) => {
      element.classList.toggle("hidden", element.dataset.jpSizeBaseHidden === "true");
      element.toggleAttribute(
        "hidden",
        element.dataset.jpSizeBaseHiddenAttribute === "true",
      );
      const display = element.dataset.jpSizeBaseDisplay || "";
      const priority = element.dataset.jpSizeBaseDisplayPriority || "";
      if (display) {
        element.style.setProperty("display", display, priority);
      } else {
        element.style.removeProperty("display");
      }
      delete element.dataset.jpSizeBaseHidden;
      delete element.dataset.jpSizeBaseHiddenAttribute;
      delete element.dataset.jpSizeBaseDisplay;
      delete element.dataset.jpSizeBaseDisplayPriority;
    });
    card.querySelectorAll("[data-jp-size-base-href-attribute]").forEach((swatch) => {
      if (swatch.dataset.jpSizeBaseHrefAttribute === "true") {
        swatch.setAttribute("data-href", swatch.dataset.jpSizeBaseHref || "");
      } else {
        swatch.removeAttribute("data-href");
      }
      delete swatch.dataset.jpSizeBaseHref;
      delete swatch.dataset.jpSizeBaseHrefAttribute;
      delete swatch.dataset.jpSizeVariant;
    });
    delete card.dataset.jpFilteredSizeVariant;
  };

  const selectFilteredCollectionSize = async (card) => {
    if (!document.body.classList.contains("template-collection-merch")) return false;

    const selectedSizes = getSelectedCollectionSizes();
    if (selectedSizes.length === 0) {
      restoreCardSwatchesAfterSizeFilter(card);
      return false;
    }

    const handle = getCardHandle(card);
    const product = await getCollectionProduct(handle);
    if (!card.isConnected || !Array.isArray(product?.variants)) return false;

    const optionNames = (product.options || []).map((option) =>
      normalizeLabel(typeof option === "string" ? option : option?.name),
    );
    const sizeIndex = optionNames.indexOf("size");
    const colorIndex = optionNames.indexOf("color");
    if (sizeIndex < 0) return false;

    const displayedColor = normalizeLabel(getDisplayedCardSwatch(card)?.dataset.color);
    const colorRanks = new Map(
      (swatchOrders.get(handle) || []).map((color, index) => [normalizeLabel(color), index]),
    );
    const sizeRanks = new Map(selectedSizes.map((size, index) => [size, index]));
    const selectedFilteredColors = getSelectedFilteredColors(handle);
    const matchingVariants = product.variants
      .filter(
        (variant) =>
          variant.available &&
          selectedSizes.includes(normalizeLabel(variant.options?.[sizeIndex])) &&
          (selectedFilteredColors.length === 0 ||
            selectedFilteredColors.includes(normalizeLabel(variant.options?.[colorIndex]))),
      )
      .sort((left, right) => {
        const leftColor = normalizeLabel(left.options?.[colorIndex]);
        const rightColor = normalizeLabel(right.options?.[colorIndex]);
        const leftDisplayedRank = leftColor === displayedColor ? 0 : 1;
        const rightDisplayedRank = rightColor === displayedColor ? 0 : 1;
        if (leftDisplayedRank !== rightDisplayedRank) {
          return leftDisplayedRank - rightDisplayedRank;
        }

        const colorRank =
          (colorRanks.get(leftColor) ?? 999) - (colorRanks.get(rightColor) ?? 999);
        if (colorRank !== 0) return colorRank;

        return (
          (sizeRanks.get(normalizeLabel(left.options?.[sizeIndex])) ?? 999) -
          (sizeRanks.get(normalizeLabel(right.options?.[sizeIndex])) ?? 999)
        );
      });

    const variant = matchingVariants[0];
    if (!variant) return false;

    if (colorIndex >= 0) {
      const variantsByColor = new Map();
      matchingVariants.forEach((candidate) => {
        const color = normalizeLabel(candidate.options?.[colorIndex]);
        if (!variantsByColor.has(color)) variantsByColor.set(color, candidate);
      });
      filterCardSwatchesByAvailableColors(card, variantsByColor);
      selectCardColor(card, variant.options?.[colorIndex]);
    }
    updateCardVariantLinks(card, variant.id);
    requestAnimationFrame(() => {
      if (!card.isConnected) return;
      updateCardVariantLinks(card, variant.id);
      syncCardHoverImage(card);
      syncCardHoverInset(card);
    });
    card.dataset.jpFilteredSizeVariant = String(variant.id);
    return true;
  };

  const selectPreferredCollectionColor = (card) => {
    if (!document.body.classList.contains("template-collection-merch")) return false;
    if (selectFilteredCollectionColor(card)) return true;
    if (card.hasAttribute("data-active-filters") || card.querySelector("[data-active-swatch]")) {
      return false;
    }

    const preferredColor = preferredCollectionColors.get(getCardHandle(card));
    if (!preferredColor) return false;
    selectCardColor(card, preferredColor);
    return false;
  };

  const syncCards = (root) => {
    scheduleMerchFilterReconciliation();
    const cards = root.matches?.("color-swatch")
      ? [root]
      : root.querySelectorAll?.("color-swatch") || [];

    const filterSignature = getSelectedCollectionColorFilterSignature();
    if (filterSignature !== lastCollectionColorFilterSignature) {
      lastCollectionColorFilterSignature = filterSignature;
      [...cards].forEach((card) => {
        const container =
          card.closest(".collection__grid__item") || card.closest(".card__container") || card;
        container.removeAttribute("data-jp-color-filter-resolved");
        container.removeAttribute("hidden");
        container.classList.remove("jp-filtered-color-unavailable");
        container.style.removeProperty("display");
      });
    }

    [...cards].forEach((card) => {
      orderCardSwatches(card);
      const selectedFilteredColor = selectPreferredCollectionColor(card);
      if (!selectedFilteredColor) {
        syncCardImage(card);
        selectDisplayedOrFirstAvailableColor(card);
      }
      bindCardHoverSync(card);
      bindFilteredSizeCardNavigation(card);
      syncCardHoverImage(card);
      syncCardHoverInset(card);
      void selectFilteredCollectionSize(card);
    });
  };

  const syncAfterWonder = (root) => {
    customElements.whenDefined("color-swatch").then(() => {
      requestAnimationFrame(() => syncCards(root));
    });
  };

  /*
   * Product recommendations fetch and replace their cards after the document's
   * initial swatch pass. Wait for those cards, then run the same displayed-image
   * selection logic so the server-selected color also has Wonder's active
   * swatch treatment.
   */
  const syncRecommendationCards = (root) => {
    const recommendations = root.matches?.("product-recommendations")
      ? [root]
      : root.querySelectorAll?.("product-recommendations") || [];

    [...recommendations].forEach((recommendation) => {
      if (recommendation.hasAttribute("data-jp-recommendation-sync")) return;

      const sync = () => {
        if (!recommendation.isConnected || !recommendation.querySelector("color-swatch")) {
          return false;
        }
        recommendation.setAttribute("data-jp-recommendation-sync", "ready");
        syncAfterWonder(recommendation);
        return true;
      };

      if (sync()) return;

      recommendation.setAttribute("data-jp-recommendation-sync", "pending");
      const observer = new MutationObserver(() => {
        if (!sync()) return;
        observer.disconnect();
      });
      observer.observe(recommendation, { childList: true, subtree: true });
    });
  };

  const normalizeLabel = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\\/g, "/")
      .replace(/\s+/g, " ");

  // Source aliases are scoped by product and exact Color, never fuzzy/global.
  // Verified full-SKU source/Admin join: gallery-repair-20261023 evidence.
  const spreadconnectGalleryColorAliases = new Map([
    ["joshs-pickles-icon-hip-bag", new Map([
      ["sand", ["sandgrau"]], ["gray", ["mineral grey"]],
    ])],
    ["i-need-a-huge-jar-of-pickles-fitted-cotton-poly-t-shirt", new Map([
      ["washed yellow", ["pastel yellow"]],
      ["military green", ["heather military green"]],
      ["sweet cream heather", ["heather cream"]],
      ["light blue", ["heather blue"]],
      ["burgundy", ["heather burgundy"]],
    ])],
    ["i-need-a-hug", new Map([["gravel", ["rock"]]])],
    ["powered-by-pickles", new Map([
      ["gravel", ["rock"]], ["dark red", ["true red"]],
    ])],
  ]);
  const spreadconnectSharedGalleryMedia = new Map();

  const mediaAltMatchesColor = (alt, color, handle) => {
    const normalized = normalizeLabel(alt);
    const separator = normalized.lastIndexOf(" - ");
    if (separator < 0) return false;
    const label = normalized.slice(separator + 3).trim();
    const expected = normalizeLabel(color);
    if (!label || !expected) return false;
    return label === expected ||
      (spreadconnectGalleryColorAliases.get(handle)?.get(expected) || [])
        .some((alias) => label === normalizeLabel(alias));
  };

  // Verified per-product view equivalence, not an assumption about image number.
  // Evidence: gallery-continuity-20261028/view-baseline.json. New media IDs
  // deliberately fall back to the selected color's first allowed image.
  const spreadconnectGalleryViews = new Map([
    [
      "i-need-a-hug",
      [
        [
          "hero",
          "46182544277654",
          "46182544408726",
          "46182544539798",
          "46182544638102"
        ],
        [
          "model-13653",
          "46182544310422",
          "46182544441494"
        ],
        [
          "model-13541",
          "46182544343190",
          "46182544474262",
          "46182544572566"
        ],
        [
          "flat-13665",
          "46182544375958",
          "46182544507030",
          "46182544605334"
        ]
      ]
    ],
    [
      "womens-i-need-a-huge-jar-of-pickles",
      [
        [
          "hero",
          "46184579530902",
          "46184579629206",
          "46184579661974"
        ],
        [
          "model-2564-list",
          "46184579563670"
        ],
        [
          "model-2528-list",
          "46184579596438"
        ]
      ]
    ],
    [
      "joshs-pickles-unisex",
      [
        [
          "hero",
          "46182526189718",
          "46182526288022",
          "46182526320790",
          "46182526386326",
          "46182526484630",
          "46182526550166",
          "46182526582934",
          "46182526681238"
        ],
        [
          "model-13653",
          "46182526222486",
          "46182526419094",
          "46182526615702",
          "46182526714006"
        ],
        [
          "model-13541",
          "46182526255254",
          "46182526353558",
          "46182526451862",
          "46182526517398",
          "46182526648470",
          "46182526746774"
        ]
      ]
    ],
    [
      "joshs-pickles-womens",
      [
        [
          "hero",
          "46181452742806",
          "46181452906646",
          "46181452939414",
          "46181452972182",
          "46181453136022"
        ],
        [
          "model-2564-detail",
          "46181452775574",
          "46181453004950",
          "46181453168790"
        ],
        [
          "model-2564-list",
          "46181452808342",
          "46181453037718",
          "46181453201558"
        ],
        [
          "model-2528-detail",
          "46181452841110",
          "46181453070486",
          "46181453234326"
        ],
        [
          "model-2528-list",
          "46181452873878",
          "46181453103254",
          "46181453267094"
        ]
      ]
    ],
    [
      "joshs-pickles-hoodie",
      [
        [
          "hero",
          "46181451890838",
          "46181451956374",
          "46181452021910",
          "46181452087446",
          "46181452152982"
        ],
        [
          "model-front",
          "46181451923606",
          "46181451989142",
          "46181452054678",
          "46181452120214",
          "46181452185750"
        ]
      ]
    ],
    [
      "unisex-classic-t-shirt",
      [
        [
          "hero",
          "46184579268758",
          "46184579334294",
          "46184579399830",
          "46184579465366"
        ],
        [
          "flat-grey",
          "46184579301526",
          "46184579367062",
          "46184579432598",
          "46184579498134"
        ]
      ]
    ],
    [
      "baseball-3-4-length-sleeve-t-shirt",
      [
        [
          "hero",
          "46181439897750",
          "46181440028822",
          "46181440159894",
          "46181440290966",
          "46181440422038"
        ],
        [
          "model-woman",
          "46181439930518",
          "46181440061590",
          "46181440192662",
          "46181440323734",
          "46181440454806"
        ],
        [
          "model-pair",
          "46181439963286",
          "46181440094358",
          "46181440225430",
          "46181440356502",
          "46181440487574"
        ],
        [
          "model-man",
          "46181439996054",
          "46181440127126",
          "46181440258198",
          "46181440389270",
          "46181440520342"
        ]
      ]
    ],
    [
      "powered-by-pickles-baby-onesie",
      [
        [
          "hero",
          "46260838432918",
          "46260838531222",
          "46260838596758",
          "46260838629526",
          "46260838662294"
        ],
        [
          "model-24067",
          "46260838465686",
          "46260838563990",
          "46260838695062"
        ],
        [
          "flat-6013",
          "46260838498454"
        ]
      ]
    ],
    [
      "joshs-pickles-beanie",
      [
        [
          "hero",
          "46181366333590",
          "46181366431894",
          "46181366530198",
          "46181366628502"
        ],
        [
          "stool-scene",
          "46181366366358",
          "46181366464662",
          "46181366562966",
          "46181366661270"
        ],
        [
          "table-scene",
          "46181366399126",
          "46181366497430",
          "46181366595734",
          "46181366694038"
        ]
      ]
    ],
    [
      "joshs-pickles-logo-sticker",
      [
        [
          "hero",
          "46181364301974",
          "46181364400278",
          "46181364498582"
        ],
        [
          "notebook-scene",
          "46181364334742",
          "46181364433046",
          "46181364531350"
        ],
        [
          "laptop-scene",
          "46181364367510",
          "46181364465814",
          "46181364564118"
        ]
      ]
    ],
    [
      "joshs-pickles-logo-snapback-baseball-cap",
      [
        [
          "hero",
          "46181362827414",
          "46181363024022",
          "46181363220630",
          "46181363384470",
          "46181363581078"
        ],
        [
          "model-10475",
          "46181362860182",
          "46181363056790",
          "46181363417238",
          "46181363613846"
        ],
        [
          "desk-1723",
          "46181362892950",
          "46181363089558",
          "46181363253398",
          "46181363450006",
          "46181363646614"
        ],
        [
          "under-brim-2882",
          "46181362925718",
          "46181363122326",
          "46181363286166",
          "46181363482774",
          "46181363679382"
        ],
        [
          "table-5288",
          "46181362958486",
          "46181363155094",
          "46181363318934",
          "46181363515542",
          "46181363712150"
        ],
        [
          "angle-11103",
          "46181362991254",
          "46181363187862",
          "46181363351702",
          "46181363548310",
          "46181363744918"
        ]
      ]
    ],
    [
      "joshs-pickles-logo-trucker-hat",
      [
        [
          "hero",
          "46181359616150",
          "46181359714454",
          "46181359812758",
          "46181359911062",
          "46181360009366",
          "46181360107670",
          "46181360205974",
          "46181360304278",
          "46181360402582"
        ],
        [
          "table-scene",
          "46181359648918",
          "46181359747222",
          "46181359845526",
          "46181359943830",
          "46181360042134",
          "46181360140438",
          "46181360238742",
          "46181360337046",
          "46181360435350"
        ],
        [
          "desk-scene",
          "46181359681686",
          "46181359779990",
          "46181359878294",
          "46181359976598",
          "46181360074902",
          "46181360173206",
          "46181360271510",
          "46181360369814",
          "46181360468118"
        ]
      ]
    ],
    [
      "joshs-pickles-logo-kids-trucker-cap",
      [
        [
          "hero",
          "46181356929174",
          "46181357027478",
          "46181357125782",
          "46181357224086",
          "46181357322390"
        ],
        [
          "table-scene",
          "46181356961942",
          "46181357060246",
          "46181357158550",
          "46181357256854",
          "46181357355158"
        ],
        [
          "bedroom-scene",
          "46181356994710",
          "46181357093014",
          "46181357191318",
          "46181357289622",
          "46181357387926"
        ]
      ]
    ],
    [
      "joshs-pickles-logo-kids-hoodie",
      [
        [
          "hero",
          "46181351489686",
          "46181351587990",
          "46181351686294",
          "46181351784598",
          "46181351882902",
          "46181351915670"
        ],
        [
          "model-2222-list",
          "46181351522454",
          "46181351620758",
          "46181351719062",
          "46181351817366",
          "46181351948438"
        ],
        [
          "model-2240-list",
          "46181351555222",
          "46181351653526",
          "46181351751830",
          "46181351850134",
          "46181351981206"
        ]
      ]
    ],
    [
      "joshs-pickles-icon-hip-bag",
      [
        [
          "hero",
          "46184599093398",
          "46184599224470",
          "46184599355542",
          "46184599486614"
        ],
        [
          "table-scene",
          "46184599126166",
          "46184599257238",
          "46184599388310",
          "46184599519382"
        ],
        [
          "cafe-scene",
          "46184599158934",
          "46184599290006",
          "46184599421078",
          "46184599552150"
        ],
        [
          "model-woman",
          "46184599191702",
          "46184599322774",
          "46184599453846",
          "46184599584918"
        ]
      ]
    ]
  ]);

  const selectSpreadconnectGalleryMedia = (handle, activeMediaId, allowedMediaIds) => {
    const ids = allowedMediaIds.map(String);
    const active = activeMediaId == null ? "" : String(activeMediaId);
    if (ids.includes(active)) return active;
    const view = spreadconnectGalleryViews.get(handle)?.find(
      ([, ...mediaIds]) => mediaIds.includes(active),
    );
    const matches = view?.slice(1).filter((id) => ids.includes(id)) || [];
    return matches.length === 1 ? matches[0] : ids[0];
  };

  const getProductColorItems = (variantOptions) => {
    const colorInputs = [...variantOptions.querySelectorAll('input[name="Color"]')];
    const colorFieldset = colorInputs[0]?.closest("fieldset");
    if (!colorFieldset) return [];

    return colorInputs
      .map((input) => input.closest("li"))
      .filter(Boolean);
  };

  const orderProductSwatches = (variantOptions) => {
    const order = swatchOrders.get(variantOptions.dataset.productHandle);
    const items = getProductColorItems(variantOptions);
    const list = items[0]?.parentElement;
    if (!order || !list) return;

    const ranks = new Map(order.map((color, index) => [normalizeLabel(color), index]));
    items
      .sort((left, right) => {
        const leftColor = normalizeLabel(left.querySelector('input[name="Color"]')?.value);
        const rightColor = normalizeLabel(right.querySelector('input[name="Color"]')?.value);
        return (ranks.get(leftColor) ?? 999) - (ranks.get(rightColor) ?? 999);
      })
      .forEach((item) => {
        const color = normalizeLabel(item.querySelector('input[name="Color"]')?.value);
        item.style.order = String(ranks.get(color) ?? 999);
        list.append(item);
      });
  };

  const getSelectedOptions = (variantOptions) =>
    [...variantOptions.querySelectorAll(".product-form__input")].map(
      (group) => group.querySelector(":checked")?.value || "",
    );

  const getSelectedVariantFeaturedMediaId = (variantOptions) => {
    const script = [...variantOptions.querySelectorAll('script[type="application/json"]')]
      .find((item) => !item.hasAttribute("data-selected-variant"));
    if (!script) return null;
    try {
      const variants = JSON.parse(script.textContent || "[]");
      const selected = getSelectedOptions(variantOptions).map(normalizeLabel);
      if (!Array.isArray(variants) || !selected.length || selected.some((v) => !v)) return null;
      return variants.find((variant) => variant.options?.length === selected.length &&
        selected.every((value, i) => normalizeLabel(variant.options[i]) === value))
        ?.featured_media?.id ?? null;
    } catch (_error) {
      return null;
    }
  };

  /*
   * Spreadconnect supplies useful per-color media labels but not Wonder's
   * hashtag convention, so Wonder normally leaves every color in one long
   * rail. Keep the selected color's mockup/model/detail images plus genuinely
   * shared media. The source product media remains untouched and returns as
   * soon as another swatch is selected.
   */
  const curateSpreadconnectGallery = (variantOptions) => {
    const productSection = variantOptions.closest("section[data-product-handle]");
    const gallery = productSection?.querySelector("gallery-section");
    if (!gallery?.filterSlides || !gallery.filterSlidesByOptions) return;

    if (!gallery.hasAttribute("data-jp-curated-gallery")) {
      gallery.filterSlidesByOptions = (slides, options, featuredMediaId, matchAll = true) => {
        const selectedColor = normalizeLabel(
          variantOptions.querySelector('input[name="Color"]:checked')?.value,
        );
        const knownColors = [...variantOptions.querySelectorAll('input[name="Color"]')]
          .map((input) => normalizeLabel(input.value))
          .filter(Boolean);

        const hasSelectedColor = Boolean(selectedColor && knownColors.length);
        gallery.toggleAttribute("data-jp-strict-color-gallery", hasSelectedColor);
        if (!hasSelectedColor) return slides;
        const handle = productSection.dataset.productHandle;
        const selectedVariantMediaId = getSelectedVariantFeaturedMediaId(variantOptions);

        return slides.filter((slide) => {
          const media = slide.querySelector("img, video");
          const mediaId = slide.dataset?.mediaId || slide.dataset?.slideMediaId ||
            media?.getAttribute("data-media-id");
          if (selectedVariantMediaId !== null &&
              String(mediaId) === String(selectedVariantMediaId)) return true;
          if ((spreadconnectSharedGalleryMedia.get(handle) || []).includes(String(mediaId))) return true;
          // Unknown or unlabeled media is NOT shared. Ignore a stale caller's
          // featuredMediaId: only the actual current variant may supply an anchor.
          return mediaAltMatchesColor(media?.getAttribute("alt"), selectedColor, handle);
        });
      };
      const originalFilterSlides = gallery.filterSlides;
      gallery.filterSlides = (options, featuredMediaId, matchAll = true, callback) => {
        const activeSlide = gallery.querySelector("[data-gallery] .swiper-slide-active");
        const activeMediaId = activeSlide?.dataset?.mediaId ||
          activeSlide?.querySelector("[data-media-id]")?.getAttribute("data-media-id");
        const allowed = gallery.filterSlidesByOptions(
          [...(gallery.elements?.gallerySlides || [])], options, false, matchAll,
        );
        const allowedIds = allowed.map((slide) => slide.dataset?.mediaId ||
          slide.querySelector("[data-media-id]")?.getAttribute("data-media-id")).filter(Boolean);
        const mediaId = selectSpreadconnectGalleryMedia(
          productSection.dataset.productHandle, activeMediaId, allowedIds,
        );
        // Commit the selected-color set and equivalent view in the same render.
        // No featured-image sorting, deferred second jump, or ordinal guessing.
        return originalFilterSlides.call(gallery, options, false, matchAll, callback,
          { mediaId, immediate: true });
      };
      gallery.setAttribute("data-jp-curated-gallery", "");
    }

    gallery.filterSlides(getSelectedOptions(variantOptions), false, false);
  };

  const syncSpreadconnectPresentation = (root) => {
    const pickers = root.matches?.("variant-options[data-jp-spreadconnect]")
      ? [root]
      : root.querySelectorAll?.("variant-options[data-jp-spreadconnect]") || [];
    [...pickers].forEach((variantOptions) => {
      orderProductSwatches(variantOptions);
      curateSpreadconnectGallery(variantOptions);
    });
  };

  /*
   * Wonder calculates option availability from left to right, which means a
   * selected color updates sizes but a selected size does not update colors.
   * For standard-size Spreadconnect catalogs, compare each option against all
   * other current selections so availability works in both directions.
   * High-variant products stay on Wonder's server-rendered path because their
   * complete variant matrix is intentionally not present in the page HTML.
   */
  const getSpreadconnectVariantData = (variantOptions) => {
    if (
      !variantOptions?.matches?.("variant-options[data-jp-spreadconnect]") ||
      variantOptions.hasAttribute("data-high-variants-product")
    ) {
      return null;
    }

    try {
      const variants = variantOptions.getVariantData?.();
      return Array.isArray(variants) ? variants : null;
    } catch (_error) {
      return null;
    }
  };

  const syncSpreadconnectOptionAvailability = (variantOptions) => {
    const variants = getSpreadconnectVariantData(variantOptions);
    if (!variants) return;

    const optionGroups = [...variantOptions.querySelectorAll(".product-form__input")];
    const selectedValues = optionGroups.map((group) =>
      normalizeLabel(group.querySelector(":checked")?.value),
    );

    optionGroups.forEach((group, optionIndex) => {
      const candidates = group.querySelectorAll('input[type="radio"], option');

      candidates.forEach((candidate) => {
        const candidateValue = normalizeLabel(candidate.value);
        const isAvailable = variants.some((variant) => {
          if (!variant.available) return false;
          if (normalizeLabel(variant.options?.[optionIndex]) !== candidateValue) return false;

          return selectedValues.every((selectedValue, selectedIndex) => {
            if (selectedIndex === optionIndex || !selectedValue) return true;
            return normalizeLabel(variant.options?.[selectedIndex]) === selectedValue;
          });
        });

        candidate.classList.toggle("disabled", !isAvailable);
      });
    });
  };

  const selectOptionValue = (group, value) => {
    const normalizedValue = normalizeLabel(value);
    const radio = [...group.querySelectorAll('input[type="radio"]')].find(
      (input) => normalizeLabel(input.value) === normalizedValue,
    );
    if (radio) {
      radio.checked = true;
      return radio;
    }

    const option = [...group.querySelectorAll("option")].find(
      (input) => normalizeLabel(input.value) === normalizedValue,
    );
    const select = option?.closest("select");
    if (!select) return null;

    select.value = option.value;
    return select;
  };

  /*
   * The shopper's most recent choice is authoritative. If that choice makes
   * the current combination unavailable, retain it and move the other option
   * to the first sellable value in the merchant-defined variant order.
   */
  const resolveUnavailableSpreadconnectSelection = (variantOptions) => {
    const variants = getSpreadconnectVariantData(variantOptions);
    const optionGroups = [
      ...variantOptions.querySelectorAll(".product-form__input"),
    ];
    const selectedValues = optionGroups.map((group) =>
      normalizeLabel(group.querySelector(":checked")?.value),
    );
    const lastChangedIndex = Number(variantOptions.dataset.jpLastChangedOption);

    if (
      !variants ||
      !Number.isInteger(lastChangedIndex) ||
      lastChangedIndex < 0 ||
      lastChangedIndex >= optionGroups.length
    ) {
      return false;
    }

    const currentSelectionIsAvailable = variants.some(
      (variant) =>
        variant.available &&
        selectedValues.every(
          (selectedValue, index) =>
            normalizeLabel(variant.options?.[index]) === selectedValue,
        ),
    );
    if (currentSelectionIsAvailable) return false;

    const preferredVariant = variants.find(
      (variant) =>
        variant.available &&
        normalizeLabel(variant.options?.[lastChangedIndex]) ===
          selectedValues[lastChangedIndex],
    );
    if (!preferredVariant) return false;

    let changeTarget = null;
    optionGroups.forEach((group, optionIndex) => {
      if (
        optionIndex === lastChangedIndex ||
        normalizeLabel(preferredVariant.options?.[optionIndex]) ===
          selectedValues[optionIndex]
      ) {
        return;
      }

      changeTarget =
        selectOptionValue(group, preferredVariant.options?.[optionIndex]) || changeTarget;
    });
    if (!changeTarget) return false;

    variantOptions.dataset.jpAutoResolving = "true";
    try {
      changeTarget.dispatchEvent(new Event("change", { bubbles: true }));
    } finally {
      delete variantOptions.dataset.jpAutoResolving;
    }
    return true;
  };

  const syncSpreadconnectAvailability = (root) => {
    const pickers = root.matches?.("variant-options[data-jp-spreadconnect]")
      ? [root]
      : root.querySelectorAll?.("variant-options[data-jp-spreadconnect]") || [];
    [...pickers].forEach(syncSpreadconnectOptionAvailability);
  };

  const syncAvailabilityAfterWonder = (root) => {
    Promise.all([
      customElements.whenDefined("variant-options"),
      customElements.whenDefined("gallery-section"),
    ]).then(() => {
      // Curate in this microtask, before paint, rather than leaving the full
      // cross-color thumbnail rail visible for two animation frames.
      syncSpreadconnectPresentation(root);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          syncSpreadconnectAvailability(root);
          syncSpreadconnectPresentation(root);
        });
      });
    });
  };

  const hoverImageObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ target }) => {
      const card = target.closest("color-swatch");
      if (card) {
        syncCardHoverInset(card);
        syncCardImageLabels(card);
      }
    });
  });

  hoverImageObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["src"],
    subtree: true,
  });

  /*
   * Wonder replaces the collection grid after every filter or sort request.
   * Reapply the merch ordering and active shade to the newly rendered cards,
   * including responses served from Wonder's in-page filter cache.
   */
  const collectionGrid = document.getElementById("ProductGridContainer");
  if (collectionGrid) {
    new MutationObserver(() => {
      syncAfterWonder(collectionGrid);
      scheduleMerchFilterReconciliation();
    }).observe(collectionGrid, {
      childList: true,
    });
  }

  document.addEventListener(
    "change",
    (event) => {
      const control = event.target.closest?.('input[type="radio"], select');
      const variantOptions = control?.closest(
        "variant-options[data-jp-spreadconnect]:not([data-high-variants-product])",
      );
      if (!variantOptions || variantOptions.dataset.jpAutoResolving) return;

      const optionGroups = [
        ...variantOptions.querySelectorAll(".product-form__input"),
      ];
      const changedGroup = control.closest(".product-form__input");
      const changedIndex = optionGroups.indexOf(changedGroup);
      if (changedIndex >= 0) {
        variantOptions.dataset.jpLastChangedOption = String(changedIndex);
      }
    },
    true,
  );

  /*
   * Spreadconnect labels product media as "Product - color". When a shopper
   * chooses one of those thumbnails, keep the selected color variant aligned
   * while leaving unlabelled lifestyle/detail media independent.
   */
  /*
   * Wonder's thumbnail carousel may stop a bubbled click after it has
   * changed the visible media. Listen during capture so the matching color
   * selection is never skipped.
   */
  document.addEventListener("click", (event) => {
    const thumbnail = event.target.closest(
      "gallery-section [data-thumbs] [data-slide-media-id]",
    );
    if (!thumbnail) return;

    const gallery = thumbnail.closest("gallery-section");
    const productSection = gallery?.closest("section[data-product-handle]");
    const handle = productSection?.dataset.productHandle;
    if (!productSection || !handle) return;

    const variantOptions = productSection.querySelector(
      `variant-options[data-product-handle="${CSS.escape(handle)}"]`,
    );
    const imageAlt = normalizeLabel(thumbnail.querySelector("img")?.alt);
    if (!variantOptions?.hasAttribute("data-jp-spreadconnect") || !imageAlt) return;

    const colorInput = [...variantOptions.querySelectorAll('input[name="Color"]')].find(
      (input) => mediaAltMatchesColor(imageAlt, input.value, handle),
    );
    if (!colorInput || colorInput.checked || colorInput.disabled) return;

    /*
     * Let Wonder finish this thumbnail click with its normal Swiper behavior.
     * The deferred color change must not rebuild or reposition the gallery,
     * because that would reset both Swipers before the clicked media is shown.
     */
    const mediaId = thumbnail.dataset.slideMediaId;
    const originalFilterSlides = gallery.filterSlides;
    gallery.filterSlides = () => {};
    variantOptions.dataset.jpThumbnailSelection = mediaId;

    const restoreGalleryFiltering = () => {
      gallery.filterSlides = originalFilterSlides;
    };

    const finishThumbnailSelection = (variantEvent) => {
      if (variantEvent.target !== variantOptions) return;

      try {
        restoreGalleryFiltering();
        // A thumbnail can select a Color from a full/unselected gallery. Now
        // commit the exact-color set too, while retaining the clicked image.
        gallery.filterSlides(getSelectedOptions(variantOptions), false, false);
        requestAnimationFrame(() => gallery.setActiveMedia?.(mediaId));
      } finally {
        delete variantOptions.dataset.jpThumbnailSelection;
        document.removeEventListener(
          "variantChangeEnd",
          finishThumbnailSelection,
        );
      }
    };
    document.addEventListener(
      "variantChangeEnd",
      finishThumbnailSelection,
    );

    Promise.resolve().then(() => {
      try {
        colorInput.click();
      } catch (error) {
        restoreGalleryFiltering();
        delete variantOptions.dataset.jpThumbnailSelection;
        document.removeEventListener(
          "variantChangeEnd",
          finishThumbnailSelection,
        );
        throw error;
      }
    });
  }, true);

  // The gallery's synchronous curator retains the current verified view for
  // swatch and size changes. Do not schedule a second reset to image one here.
  document.addEventListener("variantChangeEnd", (event) => {
    const variantOptions = event.target;
    if (!variantOptions?.matches?.("variant-options[data-jp-spreadconnect]")) return;
    if (resolveUnavailableSpreadconnectSelection(variantOptions)) return;
    syncSpreadconnectOptionAvailability(variantOptions);
  });

  /*
   * Shopify updates each facet value's result count after every selection.
   * Keep zero-result values visible so the list does not jump around, but
   * make them unavailable and visually subordinate. Wonder's footer button
   * only closes the already-live drawer, so label it with that outcome rather
   * than implying that it applies a pending selection.
   */
  const enhanceMerchFilterDrawer = () => {
    if (!document.body.classList.contains("template-collection-merch")) return;

    const form = document.getElementById("FacetFiltersForm");
    if (!form) return;

    if (!document.getElementById("jp-merch-filter-feedback-styles")) {
      const styles = document.createElement("style");
      styles.id = "jp-merch-filter-feedback-styles";
      styles.textContent = `
        .template-collection-merch #FacetFiltersForm label[data-jp-empty-filter] {
          cursor: not-allowed;
          opacity: 0.42;
        }
      `;
      document.head.appendChild(styles);
    }

    form.querySelectorAll('label.form-checkbox').forEach((label) => {
      const input = label.querySelector('input[type="checkbox"]');
      const valueLabel = label.querySelector(".form-checkbox__label");
      if (!input || !valueLabel) return;

      const hasNoResults = /\(\s*0\s*\)\s*$/.test(valueLabel.textContent || "");
      if (hasNoResults && !input.checked) {
        input.disabled = true;
        input.setAttribute("aria-disabled", "true");
        label.setAttribute("data-jp-empty-filter", "");
        return;
      }

      if (label.hasAttribute("data-jp-empty-filter")) {
        input.disabled = false;
        input.removeAttribute("aria-disabled");
        label.removeAttribute("data-jp-empty-filter");
      }
    });

    scheduleMerchFilterReconciliation();
  };

  /*
   * Wonder replaces every filter group except the one that changed. Preserve
   * all disclosure panels the shopper had open so building a multi-filter
   * query does not require reopening Product type, Size, or Color after each
   * checkbox selection.
   */
  const preserveMerchFilterAccordionState = () => {
    if (!document.body.classList.contains("template-collection-merch")) return;

    const form = document.getElementById("FacetFiltersForm");
    if (!form || form.hasAttribute("data-jp-filter-state")) return;
    form.setAttribute("data-jp-filter-state", "");

    let openIndexes = [];
    document.addEventListener(
      "input",
      (event) => {
        const input = event.target.closest?.('input[type="checkbox"]');
        if (!input || !form.contains(input)) return;

        openIndexes = [...form.querySelectorAll(".js-filter[data-index]")]
          .filter((section) =>
            section.querySelector(':scope > .filter__header[aria-expanded="true"]'),
          )
          .map((section) => section.dataset.index)
          .filter((index, position, indexes) => indexes.indexOf(index) === position);
      },
      true,
    );

    let restoreScheduled = false;
    const restoreOpenAccordions = () => {
      restoreScheduled = false;
      enhanceMerchFilterDrawer();
      if (openIndexes.length === 0) return;

      form.querySelectorAll(".js-filter[data-index]").forEach((section) => {
        const trigger = section.querySelector(":scope > .filter__header");
        if (!trigger || !openIndexes.includes(section.dataset.index)) return;

        section.dataset.open = "true";
        trigger.classList.add("wt-collapse__trigger--active");
        trigger.setAttribute("aria-expanded", "true");
      });
    };

    new MutationObserver(() => {
      if (restoreScheduled) return;
      restoreScheduled = true;
      requestAnimationFrame(restoreOpenAccordions);
    }).observe(form, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        syncAfterWonder(document);
        syncRecommendationCards(document);
        syncAvailabilityAfterWonder(document);
        enhanceMerchFilterDrawer();
        preserveMerchFilterAccordionState();
      },
      { once: true },
    );
  } else {
    syncAfterWonder(document);
    syncRecommendationCards(document);
    syncAvailabilityAfterWonder(document);
    enhanceMerchFilterDrawer();
    preserveMerchFilterAccordionState();
  }

  document.addEventListener("shopify:section:load", (event) => {
    syncAfterWonder(event.target);
    syncRecommendationCards(event.target);
    syncAvailabilityAfterWonder(event.target);
    enhanceMerchFilterDrawer();
  });
})();

